import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Buildings, CheckCircle, Clock, Warning, MapPin } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_LABEL = {
  received: { label: "Received", color: "#2563EB" },
  open: { label: "Received — emergency dispatch", color: "#dc2626" },
  acknowledged: { label: "Acknowledged by team", color: "#d97706" },
  pending: { label: "Awaiting scheduling", color: "#d97706" },
  scheduled: { label: "Scheduled", color: "#2563EB" },
  in_progress: { label: "In progress", color: "#7c3aed" },
  submitted: { label: "Logged with our team", color: "#2563EB" },
  completed: { label: "Completed", color: "#059669" },
  cancelled: { label: "Cancelled", color: "#64748b" },
};

export default function TenantReportTrackerPage() {
  const { trackingToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND}/api/tenant-report/track/${trackingToken}`);
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Tracking link not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [trackingToken]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="max-w-md text-center">
        <Warning className="h-12 w-12 text-red-500 mx-auto mb-3" />
        <h1 className="text-2xl font-semibold mb-2">Tracking unavailable</h1>
        <p className="text-sm text-slate-500">{error}</p>
      </div>
    </div>
  );

  const status = STATUS_LABEL[data.live_status] || { label: data.live_status, color: "#64748b" };
  const sevColor = data.severity === "critical" ? "#dc2626" : data.severity === "urgent" ? "#d97706" : "#2563EB";

  return (
    <div className="min-h-screen bg-background py-6" data-testid="trp-tracker-page">
      <div className="max-w-2xl mx-auto px-4 space-y-5">
        <div className="text-center pt-2">
          <div className="h-12 w-12 rounded-md flex items-center justify-center mx-auto mb-3 bg-primary">
            <Buildings className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Report Status</h1>
          <p className="text-sm text-slate-500 mt-1">No login required — keep this link to check back.</p>
        </div>

        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="text-xs text-slate-500">Reference</p>
              <p className="font-mono font-semibold" data-testid="trp-tracker-ref">{data.reference}</p>
            </div>
            <span className="text-[10px] px-2 py-1 rounded-full font-medium uppercase tracking-wider"
                  style={{ backgroundColor: `${sevColor}20`, color: sevColor }}>
              {(data.severity || "").replace("_", " ")}
            </span>
          </div>
          <div>
            <p className="text-xs text-slate-500">Issue</p>
            <p className="font-semibold" data-testid="trp-tracker-title">{data.title}</p>
            <p className="text-xs text-slate-500 mt-0.5">{data.category}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> Address</p>
            <p className="text-sm">
              {[data.address?.street_address, data.address?.town_city, data.address?.postcode].filter(Boolean).join(", ") || "—"}
            </p>
          </div>
          {data.notify_whatsapp && (
            <div className="text-xs bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-md p-2.5 text-emerald-700 dark:text-emerald-300" data-testid="trp-tracker-whatsapp-on">
              💬 WhatsApp updates are ON — you'll get a message when work is scheduled.
            </div>
          )}
        </div>

        {/* Status */}
        <div className="bg-card border border-border rounded-xl p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Current status</p>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${status.color}20`, color: status.color }}>
              {data.live_status === "completed" ? <CheckCircle className="h-5 w-5" weight="fill" /> : <Clock className="h-5 w-5" />}
            </div>
            <div>
              <p className="text-base font-semibold" style={{ color: status.color }} data-testid="trp-tracker-status">{status.label}</p>
              <p className="text-xs text-slate-500">Last update: {(data.last_update || "").slice(0, 16).replace("T", " ")}</p>
            </div>
          </div>
        </div>

        {/* Timeline */}
        {(data.timeline || []).length > 0 && (
          <div className="bg-card border border-border rounded-xl p-5 space-y-3">
            <h2 className="text-sm font-semibold">Activity</h2>
            <div className="space-y-3">
              {data.timeline.slice().reverse().map((t, i) => (
                <div key={i} className="text-xs border-l-2 border-border pl-3 py-0.5">
                  <p className="text-slate-700 dark:text-slate-300">{t.description}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{(t.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[11px] text-center text-slate-400 pb-4">Submitted {(data.submitted_at || "").slice(0, 10)} · Powered by Fixlr</p>
      </div>
    </div>
  );
}
